import {
  StandardRPCJsonSerializer,
  StandardRPCLinkCodec,
  StandardRPCSerializer,
} from '@orpc/client/standard';
import config from '@/config';
import { buildRpcUrl } from './rpcUrl';

/**
 * Builds the plain HTTP GET URL for an oRPC procedure that streams a file
 * download.
 *
 * These procedures resolve to a byte stream rather than a JSON value, so they
 * are triggered as a browser navigation (`window.open`) instead of through the
 * typed client: the server sets `Content-Disposition: attachment`, so the
 * browser streams the response straight to disk with its own progress UI. A
 * `fetch(...).blob()` would instead buffer the whole file in memory and show no
 * feedback until it finished — indistinguishable from broken on an export that
 * takes minutes.
 *
 * The URL is produced by oRPC's own request codec, so the path and the
 * `?data=` payload match exactly what the RPC handler expects.
 */

// Comfortably under the 16KB request-header budget Node allows by default,
// with room for cookies and the rest of the request line.
//
// Exported so the id-list caps that have to fit inside it can be tested against
// the real number instead of a restated copy of it.
export const MAX_DOWNLOAD_URL_LENGTH = 8000;

const codec = new StandardRPCLinkCodec<Record<never, never>>(
  new StandardRPCSerializer(new StandardRPCJsonSerializer()),
  {
    url: () => buildRpcUrl(config.apiBaseUrl),
    method: 'GET',
    // Past this the codec would fall back to a POST with a body, which a
    // navigation can't send — better to fail loudly (see below).
    maxUrlLength: MAX_DOWNLOAD_URL_LENGTH,
  },
);

export async function buildRpcDownloadUrl(
  path: readonly string[],
  input: unknown,
): Promise<string> {
  const request = await codec.encode(path, input, { context: {} });
  if (request.method !== 'GET') {
    throw new Error(
      'The current filters are too long to put in a download URL. Narrow them and try again.',
    );
  }
  return request.url.toString();
}

/**
 * Start a streaming download WITHOUT depending on transient user activation.
 *
 * `startRpcDownload` uses `window.open`, which browsers only allow while the
 * click's activation is still valid — Safari drops it after any `await`, Chrome
 * after roughly five seconds. A caller that must ask the server something first
 * (an "is there anything to export" pre-check, which on a month-scale range can
 * take seconds) would have its download blocked instead of started.
 *
 * Navigating a throwaway hidden frame needs no activation: the response carries
 * `Content-Disposition: attachment`, so the browser takes it as a download and
 * the page never moves.
 *
 * Frames are retired by AGE, never by completion, because there is no
 * completion signal to use: an attachment download aborts the navigation, so
 * the frame's `load` never fires, and a frame still on `about:blank` looks the
 * same whether the download has been handed to the browser or the request is
 * simply still in flight. So any cleanup rule is a deadline — the question is
 * only how generous it is.
 *
 * What matters for safety is time-to-HEADERS, not time-to-transfer: once the
 * browser has the `Content-Disposition` response the download belongs to its
 * download manager and the frame is irrelevant, however long the bytes take
 * after that. {@link FRAME_RETIREMENT_MS} is set orders of magnitude above any
 * plausible time-to-headers, so eviction can only ever reach a frame whose
 * download was handed off long ago (or was never going to arrive).
 *
 * That bounds retained frames by "downloads started in the last few minutes"
 * rather than "downloads for the life of the page" — an iframe is a retained
 * browsing context, not just a DOM node, so on a long-lived reports page the
 * unbounded version was a real if slow leak.
 */
export const MAX_RETAINED_FRAMES = 4;
const FRAME_RETIREMENT_MS = 10 * 60 * 1000;

const retainedFrames: { frame: HTMLIFrameElement; startedAt: number }[] = [];

/**
 * Drop frames past the cap, oldest first, but only once they are older than
 * {@link FRAME_RETIREMENT_MS}. Stops at the first frame too young to touch, so
 * a burst of downloads is retained in full rather than cancelling each other.
 */
function retireOldFrames(now: number): void {
  while (retainedFrames.length > MAX_RETAINED_FRAMES) {
    const oldest = retainedFrames[0];
    if (!oldest || now - oldest.startedAt < FRAME_RETIREMENT_MS) return;
    retainedFrames.shift();
    oldest.frame.remove();
  }
}

export async function startRpcDownloadInFrame(
  path: readonly string[],
  input: unknown,
): Promise<void> {
  const url = await buildRpcDownloadUrl(path, input);
  startDownloadInFrame(url);
}

/** Retain attachment downloads with the shared age-and-cap cleanup policy. */
export function startDownloadInFrame(url: string): void {
  const frame = document.createElement('iframe');
  frame.hidden = true;
  frame.src = url;
  document.body.append(frame);

  const startedAt = Date.now();
  retainedFrames.push({ frame, startedAt });
  retireOldFrames(startedAt);
}

/** Test seam: the frames this module is currently holding on to. */
export function retainedDownloadFrameCount(): number {
  return retainedFrames.length;
}

/**
 * Opens a streaming download in a new tab. Rejects when the browser blocks the
 * pop-up, so callers can surface that rather than looking like a no-op.
 */
export async function startRpcDownload(
  path: readonly string[],
  input: unknown,
): Promise<void> {
  const url = await buildRpcDownloadUrl(path, input);
  const opened = window.open(url, '_blank');
  if (!opened) {
    throw new Error(
      'The browser blocked the export download. Allow pop-ups for this site and try again.',
    );
  }
}
